import { useEffect, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
	PS_Actions,
	PS_Button,
	PS_Card,
	PS_Container,
	PS_Description,
	PS_Status,
	PS_Title,
} from "./PaymentSuccessPage.styled";
import { useStores } from "../../stores";
import { API } from "../../core";

type PaymentStatus = "checking" | "success" | "pending" | "error";

type ConfirmPaymentResponse = {
	success: boolean;
	data?: {
		order_id: number;
		status: string;
		already_paid?: boolean;
	};
	errors?: string[];
};

export const PaymentSuccessPage = observer(() => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const { authStore, notificationsStore } = useStores();

	const [status, setStatus] = useState<PaymentStatus>("checking");
	const [message, setMessage] = useState("Проверяем платеж...");

	const payload = useMemo(() => {
		return {
			_payform_status: searchParams.get("_payform_status"),
			_payform_id: searchParams.get("_payform_id"),
			_payform_order_id: searchParams.get("_payform_order_id"),
			_payform_sign: searchParams.get("_payform_sign"),
		};
	}, [searchParams]);

	useEffect(() => {
		let isMounted = true;

		const confirmPayment = async () => {
			try {
				if (!payload._payform_order_id || !payload._payform_sign) {
					setStatus("error");
					setMessage("Не удалось получить данные для подтверждения платежа.");

					return;
				}

				const response = await API.post<ConfirmPaymentResponse>("shop/prodamus/confirm-return", payload);

				if (!isMounted) return;

				if (response.success && response.data?.status === "paid") {
					setStatus("success");
					setMessage("Оплата прошла успешно. Доступ активирован.");

					notificationsStore.success({
						message: "Доступ успешно активирован.",
					});

					await authStore.check();

					return;
				}

				setStatus("pending");
				setMessage("Платеж обрабатывается. Обновите страницу через несколько секунд.");
			} catch (error) {
				console.error("[Prodamus] confirm return error:", error);

				if (!isMounted) return;

				setStatus("error");
				setMessage("Не удалось подтвердить оплату. Если деньги списались, обратитесь в поддержку.");

				notificationsStore.error({
					message: "Не удалось подтвердить оплату. Если деньги списались, обратитесь в поддержку.",
				});
			}
		};

		confirmPayment();

		return () => {
			isMounted = false;
		};
	}, [authStore, notificationsStore, payload]);

	const title = {
		checking: "Проверка платежа",
		success: "Оплата завершена",
		pending: "Платеж обрабатывается",
		error: "Ошибка подтверждения",
	}[status];

	return (
		<PS_Container>
			<PS_Card>
				<PS_Status $status={status}>{status}</PS_Status>

				<PS_Title>{title}</PS_Title>

				<PS_Description>{message}</PS_Description>

				<PS_Actions>
					<PS_Button type="button" onClick={() => navigate("/cabinet/subscriptions-plans")}>
						Мои подписки
					</PS_Button>
				</PS_Actions>
			</PS_Card>
		</PS_Container>
	);
});
